// The place to ADD a sport, and the one every screen should ask. Everything
// that needs a SportDefinition asks here for it by id.
//
// This said "the one place the web app imports sport packages" from the day it
// was written, and was false from 2026-09-12: `data/SimProvider.tsx` and
// `screens/LabPlayerScreen.tsx` both import the football package directly, and
// no lint constrains them — `apps/web` is under no purity rule. A "this is the
// only place that does X" comment is an invariant with no enforcement, and it
// decays silently because the next person adds their import somewhere else and
// never opens the file that claims to be alone (AUDIT-2026-09-21).

import { positionNumbersFor, type PositionNumbers } from '@fsp/domain';
import type { ProTeam, SportDefinition, SportId, TeamPalette } from '@fsp/sports-core';
import { football } from '@fsp/sports-football';

const SPORTS: ReadonlyMap<SportId, SportDefinition> = new Map([[football.id, football]]);

/** Which numbers fit each sport's positions (`positionNumbersFor`), once per sport. */
const NUMBERS: ReadonlyMap<SportId, PositionNumbers> = new Map(
  [...SPORTS].map(([id, def]) => [id, positionNumbersFor(def)]),
);

/**
 * How a position, or a slot, reads on screen: a position as the starting
 * slot that takes it alone is named (football's `DST` reads *DEF*), anything
 * else as it is (`FLEX`, `BN`). In the league's sport, or in any sport here
 * when the caller does not know it.
 */
export function positionLabelOf(id: string, sport?: string): string {
  const all = sport === undefined ? [...NUMBERS.values()] : [NUMBERS.get(sport as SportId)];
  for (const n of all) {
    const label = n?.label(id);
    if (label !== undefined && label !== id) return label;
  }
  return id;
}

export function getSport(id: SportId): SportDefinition {
  const def = SPORTS.get(id);
  if (!def) throw new Error(`Sport not registered: ${id}`);
  return def;
}

export function listSports(): readonly SportDefinition[] {
  return [...SPORTS.values()];
}

/**
 * A club's colors, by its id. By its abbreviation too, for a theme saved,
 * on the account or in a browser, while the Rams were keyed `LAR`
 * (2026-10-25, `teams.ts`).
 */
export function lookupTeamPalette(sport: string, teamId: string): TeamPalette | undefined {
  const teams = SPORTS.get(sport as SportId)?.proTeams;
  return (teams?.find((t) => t.id === teamId) ?? teams?.find((t) => t.abbreviation === teamId))
    ?.palette;
}

/**
 * The club a code names: the code every view carries (`ProTeam.id`), in the
 * league's sport, or in any sport here when the caller does not know it.
 */
export function proTeamOf(code: string, sport?: string): ProTeam | undefined {
  const sports = sport === undefined ? listSports() : [SPORTS.get(sport as SportId)];
  for (const def of sports) {
    const team = def?.proTeams.find((t) => t.id === code);
    if (team) return team;
  }
  return undefined;
}

/**
 * A club's code as a person reads it: its abbreviation, which for the Rams is
 * `LAR` where the data says `LA` (`teams.ts`). A code no sport knows is shown
 * as it is.
 */
export function clubLabel(code: string, sport?: string): string {
  return proTeamOf(code, sport)?.abbreviation ?? code;
}

/**
 * Whether a lowercase search names a club, as stored or as it reads: `la` and
 * `lar` both find the Rams (B-510).
 */
export function clubMatches(code: string | null, needle: string): boolean {
  if (code === null) return false;
  return code.toLowerCase().includes(needle) || clubLabel(code).toLowerCase().includes(needle);
}
